import React, { useState, useEffect, useRef } from 'react';
import ModalWrapper from '../ModalWrapper';
import { Typography, Box, Alert } from '@mui/material';
import { resolveModelForProviderModel } from '../../utils/api/aiFeatureModel_apis';
import { fetchEnvironmentList } from '../../utils/api/environment_apis';
import { getTrialUsage } from '../../utils/api/trialUsage_apis';
import { getTestGroupSummary } from '../../utils/api/testgroup_apis';
import { createTestLaunch } from '../../utils/api/testlaunch_apis';
import { STATUS } from '../../utils/api/sharedfunction';
import { trackUiAction } from '../../utils/telemetryClient';
import { TelemetryActions } from '../../enums/TelemetryActionsEnum';
import { MIN_CREDITS_FOR_LAUNCH } from '../../constants/trialLimits';
import { useSnackbar } from '../../utils/useSnackbar';
import { useSelector } from 'react-redux';
import { FormSchemas, FormWrapper, CommonFooter } from '../FormComponent';
import { AI_FEATURE_KEYS } from '../../enums/AIModelEnums';
import { userCanSelectAiModel } from '../../utils/commonfunctions';
import AIModelRuntimeSelector from '../Select/llmModelSelector/AIModelRuntimeSelector';
import { LaunchType } from '../../enums/ScheduleEnums';
import typeCardStyles from '../../styles/createTestLaunch/launchTypeCards.module.scss';
import launchStyles from '../../styles/createTestLaunch/createTestLaunchModal.module.scss';
import {
  LaunchTypeCheckIcon,
  LaunchTypeBanIcon,
} from './LaunchTypeIcons';
import { TYPE_OPTIONS, includedCaseCount } from './createTestLaunchShared';

function primaryButtonLabel(launchType, count) {
  if (launchType === LaunchType.AUTOMATED) {
    return count === 1 ? 'Run 1 automated test now' : `Run ${count} automated tests now`;
  }
  if (launchType === LaunchType.MANUAL) {
    return count === 1 ? 'Run 1 manual test now' : `Run ${count} manual tests now`;
  }
  return count === 1 ? 'Run 1 test now' : `Run ${count} tests now`;
}

function CreateTestLaunchModal({ open, onClose, onSubmit, group, projectId, onOpenSchedule }) {
  const { user } = useSelector((state) => state.auth);
  const canSelectAiModel = userCanSelectAiModel(user, projectId);
  const isTrial = user?.plan_name === 'trial';
  const [environments, setEnvironments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fetchingEnvironments, setFetchingEnvironments] = useState(false);
  const [testScope, setTestScope] = useState({ total: 0, automated: 0, manual: 0 });
  const [fetchingTestScope, setFetchingTestScope] = useState(false);
  const [modalError, setModalError] = useState('');
  const [creditsRemaining, setCreditsRemaining] = useState(null);
  const [trialExpired, setTrialExpired] = useState(false);
  const [launchType, setLaunchType] = useState(LaunchType.COMPLETE);
  const [aiModelSelection, setAiModelSelection] = useState({ model: null, meta: {} });
  const [aiSelectorKey, setAiSelectorKey] = useState(0);
  const formRef = useRef(null);
  const { showSnackbar } = useSnackbar();

  const compositionCount = includedCaseCount(launchType, testScope);
  const nlpCountForCredits = launchType === LaunchType.MANUAL ? 0 : (testScope.automated || 0);
  const requiredCreditsForLaunch = MIN_CREDITS_FOR_LAUNCH * nlpCountForCredits;
  const needsAiModel = launchType !== LaunchType.MANUAL && (testScope.automated || 0) > 0;
  const hasResolvedAiModel = Boolean(
    (aiModelSelection.model?.provider && aiModelSelection.model?.model_key) ||
    aiModelSelection.meta?.serverResolve
  );
  const canStartTestLaunch = !isTrial
    ? true
    : !trialExpired && (creditsRemaining ?? 0) >= requiredCreditsForLaunch;

  useEffect(() => {
    if (open && isTrial) {
      getTrialUsage()
        .then((res) => {
          if (res?.status === 'success' && res?.data) {
            setCreditsRemaining(res.data.credits_remaining != null ? Number(res.data.credits_remaining) : 0);
            setTrialExpired(Boolean(res.data.is_expired));
          } else {
            setCreditsRemaining(null);
            setTrialExpired(false);
          }
        })
        .catch(() => {
          setCreditsRemaining(null);
          setTrialExpired(false);
        });
    } else if (open && !isTrial) {
      setCreditsRemaining(null);
      setTrialExpired(false);
    }
  }, [open, isTrial]);

  useEffect(() => {
    if (open) {
      setLaunchType(LaunchType.COMPLETE);
      setModalError('');
      setAiModelSelection({ model: null, meta: {} });
      setAiSelectorKey((k) => k + 1);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fetchEnvironments();
      if (group?.id) {
        fetchTestScope();
      }
    }
  }, [open, projectId, group?.id]);

  useEffect(() => {
    if (!open || fetchingTestScope) return;
    const currentCount = includedCaseCount(launchType, testScope);
    if (currentCount > 0) return;
    const fallback = [LaunchType.COMPLETE, LaunchType.AUTOMATED, LaunchType.MANUAL]
      .find((type) => includedCaseCount(type, testScope) > 0);
    if (fallback) {
      setLaunchType(fallback);
      formRef.current?.setValue?.('launchType', fallback, { shouldValidate: true });
    }
  }, [open, fetchingTestScope, testScope, launchType]);

  const handleLaunchTypeSelect = (type, disabled) => {
    if (disabled) return;
    setLaunchType(type);
    setModalError('');
    formRef.current?.setValue?.('launchType', type, { shouldValidate: true });
    if (type === LaunchType.MANUAL) {
      formRef.current?.setValue?.('isSequential', false, { shouldValidate: true });
      formRef.current?.setValue?.('continueOnFailure', false, { shouldValidate: true });
    }
  };

  const handleFormSubmit = async () => {
    if (!formRef.current) return;
    try {
      if (needsAiModel && !hasResolvedAiModel && !isTrial) {
        showSnackbar(
          'No AI model configured for Test Launch. Configure a model in Administration > Configuration.',
          'error'
        );
        return;
      }
      await formRef.current.handleSubmit(handleConfirmSubmit)();
    } catch (error) {
      console.error('Form submit error:', error);
    }
  };

  const handleConfirmSubmit = async (formValues) => {
    setModalError('');
    setLoading(true);

    try {
      if (testScope.total === 0) {
        showSnackbar('Cannot create test launch for an empty test group. Please add test cases to the group first.', 'error');
        return;
      }

      if (compositionCount === 0) {
        showSnackbar('Cannot create test launch: no test cases match the selected launch type for this group.', 'error');
        return;
      }

      if (compositionCount > 100) {
        showSnackbar(`A launch cannot have more than 100 test cases. Please reduce the selection to 100 or fewer (currently ${compositionCount}).`, 'error');
        return;
      }

      if (!formValues.environment) {
        showSnackbar('Environment is required to create a test launch.', 'error');
        return;
      }

      let normalizedLlmModelId = null;
      if (aiModelSelection.model?.provider && aiModelSelection.model?.model_key) {
        normalizedLlmModelId = {
          provider: aiModelSelection.model.provider,
          model_key: aiModelSelection.model.model_key,
        };
      } else if (
        typeof formValues.aiModel === 'string' &&
        formValues.aiModel.trim().startsWith('{')
      ) {
        try {
          const parsed = JSON.parse(formValues.aiModel);
          if (parsed?.provider && parsed?.model_key) {
            normalizedLlmModelId = {
              provider: parsed.provider,
              model_key: parsed.model_key,
            };
          }
        } catch (e) {
          // Backend may resolve org default for Auto / server-resolve.
        }
      }

      if (
        normalizedLlmModelId?.provider &&
        normalizedLlmModelId?.model_key
      ) {
        const resolveRes = await resolveModelForProviderModel(
          normalizedLlmModelId.provider,
          normalizedLlmModelId.model_key,
          projectId ?? group?.project_id
        );
        const aiApiKeyId = resolveRes?.data?.data?.ai_api_key_id || null;
        if (aiApiKeyId) {
          normalizedLlmModelId = {
            ...normalizedLlmModelId,
            aiApiKeyId,
          };
        }
      }

      const config = {
        environmentId: formValues.environment,
        ...(normalizedLlmModelId && { llmModelId: normalizedLlmModelId }),
        ...(formValues.isSequential && { continueOnFailure: Boolean(formValues.continueOnFailure) }),
      };

      const hostname = window.location.hostname;
      trackUiAction(TelemetryActions.RUN_LAUNCH_CLICKED, {
        testgroupId: group?.id,
      });
      // Call the API to create test launch
      const selectedType = formValues.launchType || launchType;
      const response = await createTestLaunch({
        testgroupId: group?.id,
        name: formValues.launchName,
        isSequential: formValues.isSequential,
        launchType: selectedType,
        config,
        hostname: window.location.hostname,
      });

      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || `Test launch created successfully: ${formValues.launchName}`, 'success');
        window.dispatchEvent(new CustomEvent('trialUsageRefresh'));
        onSubmit?.({
          testgroupId: group?.id,
          name: formValues.launchName,
          isSequential: formValues.isSequential,
          launchType: selectedType,
          config,
        });
        onClose();
      } else {
        const errMsg = response.error || 'Failed to create test launch';
        setModalError(errMsg);
        showSnackbar(errMsg, 'error');
      }
    } catch (error) {
      console.error('Error creating test launch:', error);
      const errMsg = error.message || 'Failed to create test launch';
      setModalError(errMsg);
      showSnackbar(errMsg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchEnvironments = async () => {
    setFetchingEnvironments(true);
    try {
      const response = await fetchEnvironmentList();
      if (response.status === STATUS.SUCCESS) {
        setEnvironments(response.data || []);
      } else {
        showSnackbar(response.error || 'Failed to fetch environments', 'error');
      }
    } catch (error) {
      console.error('Error fetching environments:', error);
      showSnackbar('Error fetching environments', 'error');
    } finally {
      setFetchingEnvironments(false);
    }
  };

  const fetchTestScope = async () => {
    if (!group?.id) return;

    setFetchingTestScope(true);
    try {
      const response = await getTestGroupSummary({ testGroupId: group.id });
      if (response.status === STATUS.SUCCESS) {
        setTestScope(response.data || { total: 0, automated: 0, manual: 0 });
      } else {
        setTestScope({ total: 0, automated: 0, manual: 0 });
      }
    } catch (error) {
      console.error('Error fetching test scope:', error);
      showSnackbar('Error fetching test scope', 'error');
      setTestScope({ total: 0, automated: 0, manual: 0 });
    } finally {
      setFetchingTestScope(false);
    }
  };

  const getLaunchName = () => {
    const datePart = new Date().toISOString().slice(0, 10);
    const randomPart = String(Math.floor(Math.random() * 100000)).padStart(5, '0');
    const fixedLength = datePart.length + randomPart.length + 2;
    const maxGroupLength = 100 - fixedLength;
    const groupName = group?.name || '';
    const trimmedGroupName = groupName.length > maxGroupLength
      ? groupName.slice(0, maxGroupLength)
      : groupName;
    return `${trimmedGroupName}-${datePart}-${randomPart}`;
  };

  const envOptions = environments.map((env) => ({
    value: env.id,
    label: env.name,
  }));

  const nameField = {
    name: 'launchName',
    type: 'text',
    label: 'Launch Name',
    placeholder: group ? getLaunchName() : 'Enter launch name',
    required: true,
    autoFocus: true,
  };

  const buttons = [
    {
      label: 'Cancel',
      variant: 'outline',
      className: 'primary',
      onClick: onClose,
      disabled: loading,
    },
    {
      label: primaryButtonLabel(launchType, compositionCount || 0),
      variant: 'primary',
      className: 'white bg-blue600',
      onClick: handleFormSubmit,
      disabled: loading || fetchingTestScope || compositionCount === 0 || compositionCount > 100 || !canStartTestLaunch,
      loading,
    },
  ];

  return (
    <ModalWrapper
      isOpen={open}
      onClose={onClose}
      title="Run tests now"
      size="medium"
      className={launchStyles.modalWider}
      bodyClassName={launchStyles.modalBody}
      footer={
        <Box sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 1.5,
              px: 0.5,
            }}
          >
            <Typography variant="caption" color="text.secondary">
              Need this to run regularly?{' '}
              <Box
                component="button"
                type="button"
                onClick={() => {
                  if (typeof onOpenSchedule === 'function') {
                    const environmentId = formRef.current?.getValues?.()?.environment || '';
                    onOpenSchedule({
                      launchType,
                      environmentId,
                      aiModelSelection,
                    });
                  } else {
                    showSnackbar('Schedule setup is not available.', 'info');
                  }
                }}
                sx={{
                  background: 'none',
                  border: 'none',
                  p: 0,
                  color: 'primary.main',
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  font: 'inherit',
                }}
              >
                Set up a schedule
              </Box>
            </Typography>
          </Box>
          <CommonFooter buttons={buttons} align="right" />
        </Box>
      }
    >
      <div className={launchStyles.root}>
        {modalError && (
          <Alert severity="error" sx={{ mb: 0 }}>
            {modalError}
          </Alert>
        )}
        {!canStartTestLaunch && isTrial && (
          <Alert severity="warning" sx={{ mb: 0 }}>
            {trialExpired ? 'Your trial has ended. Please upgrade your plan to continue.' : 'Your credits are low. Please upgrade your plan.'}
          </Alert>
        )}

        <FormWrapper
          ref={formRef}
          className={launchStyles.formNoExtraPad}
          schema={FormSchemas.testLaunchForm(testScope, { launchType })}
          fields={[nameField]}
          onSubmit={handleConfirmSubmit}
          key={`${open ? 'open' : 'closed'}-${group?.id || 'nogroup'}`}
          defaultValues={{
            launchName: group ? getLaunchName() : '',
            launchType,
            startType: 'Immediate',
            isSequential: false,
            continueOnFailure: false,
            environment: '',
            aiModel: '',
          }}
        >
          {({ Controller, control, watch, setValue, FormComponent, errors, isSubmitting }) => {
            const isSequential = Boolean(watch('isSequential'));

            return (
              <>
                <div className={`${typeCardStyles.section} ${launchStyles.typeSection}`}>
                  <p className={typeCardStyles.sectionTitle}>What should this run cover?</p>
                  <div className={typeCardStyles.cardList} role="radiogroup" aria-label="Launch type">
                    {TYPE_OPTIONS.map((opt) => {
                      const count = testScope[opt.countKey] || 0;
                      const disabled = fetchingTestScope || count === 0;
                      const selected = launchType === opt.value;
                      const Icon = opt.Icon;
                      const className = [
                        typeCardStyles.card,
                        selected && !disabled ? typeCardStyles.selected : '',
                        disabled ? typeCardStyles.disabled : '',
                      ].filter(Boolean).join(' ');

                      return (
                        <button
                          key={opt.value}
                          type="button"
                          role="radio"
                          aria-checked={selected}
                          disabled={disabled}
                          onClick={() => handleLaunchTypeSelect(opt.value, disabled)}
                          className={className}
                        >
                          <Icon className={typeCardStyles.icon} />
                          <div className={typeCardStyles.body}>
                            <p className={typeCardStyles.title}>{opt.title}</p>
                            <p className={typeCardStyles.description}>{opt.description(count)}</p>
                          </div>
                          <div className={typeCardStyles.trailing}>
                            {disabled ? (
                              <LaunchTypeBanIcon className={typeCardStyles.banIcon} />
                            ) : selected ? (
                              <LaunchTypeCheckIcon className={typeCardStyles.checkIcon} />
                            ) : null}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {launchType !== LaunchType.MANUAL &&
                <div className={launchStyles.sequentialRow}>
                  <Controller
                    name="isSequential"
                    control={control}
                    render={({ field }) => (
                      <FormComponent
                        type="checkbox"
                        name={field.name}
                        label="Run Sequentially"
                        tooltip="Sequential mode runs tests one after another in defined order"
                        checked={Boolean(field.value)}
                        onChange={(e) => {
                          const checked = Boolean(e?.target?.checked);
                          field.onChange(checked);
                          if (checked) {
                            setValue('continueOnFailure', false, { shouldValidate: true });
                          }
                        }}
                        onBlur={field.onBlur}
                        disabled={loading || isSubmitting}
                        className={launchStyles.inlineCheckbox}
                      />
                    )}
                  />
                  {isSequential && (
                    <Controller
                      name="continueOnFailure"
                      control={control}
                      render={({ field }) => (
                        <FormComponent
                          type="checkbox"
                          name={field.name}
                          label="Continue On Failure"
                          checked={Boolean(field.value)}
                          onChange={(e) => field.onChange(Boolean(e?.target?.checked))}
                          onBlur={field.onBlur}
                          disabled={loading || isSubmitting}
                          className={launchStyles.inlineCheckbox}
                        />
                      )}
                    />
                  )}
                </div>}

                <div
                  className={`${launchStyles.envAiGrid}${needsAiModel ? '' : ` ${launchStyles.envAiGridSolo}`}`}
                >
                  <Controller
                    name="environment"
                    control={control}
                    render={({ field, fieldState }) => (
                      <FormComponent
                        type="select"
                        name={field.name}
                        label="Environment"
                        value={field.value || ''}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        options={envOptions}
                        required
                        disabled={fetchingEnvironments || loading || isSubmitting}
                        error={fieldState.error?.message || errors?.environment?.message}
                        helperText={fetchingEnvironments ? 'Loading environments...' : undefined}
                      />
                    )}
                  />

                  {needsAiModel && (
                    <div className={launchStyles.aiModelWrap}>
                      <AIModelRuntimeSelector
                        key={aiSelectorKey}
                        className={launchStyles.aiModelSelector}
                        featureKey={AI_FEATURE_KEYS.TEST_LAUNCH}
                        trialOrgSkip={isTrial}
                        canSelectAiModel={canSelectAiModel}
                        projectId={projectId ?? group?.project_id}
                        required={needsAiModel}
                        disabled={fetchingTestScope || !canSelectAiModel || isTrial || loading}
                        readOnly={!canSelectAiModel && !isTrial}
                        label="AI Model"
                        helperText={fetchingTestScope ? 'Loading test scope...' : undefined}
                        onModelChange={(model, meta = {}) => {
                          setAiModelSelection({ model: model || null, meta: meta || {} });
                          const v =
                            model?.provider && model?.model_key
                              ? JSON.stringify({ provider: model.provider, model_key: model.model_key })
                              : meta?.serverResolve
                                ? 'auto'
                                : '';
                          setValue('aiModel', v, { shouldValidate: true });
                        }}
                      />
                    </div>
                  )}
                </div>
              </>
            );
          }}
        </FormWrapper>
      </div>
    </ModalWrapper>
  );
}

export default CreateTestLaunchModal;
